import React, { useState, useEffect } from 'react'
import { Form, Input, Header, Divider, Button } from 'semantic-ui-react'
import FormLabel from '../../common/FormLabel'
import DelayPercentageRepository, {
  AddDelayPercentageRequestObject,
  UpdateDelayPercentageRequestObject,
} from '../../../common/repository/DelayPercentageRepository'
import Result from '../../../common/repository/Result'
import { toast } from '../../common/Toast'
import validator from '../../../common/validator'
import DelayPercentage from '../../../models/DelayPercentage'

function DelayPercentageSegment() {
  const delayPercentageRepo = new DelayPercentageRepository()
  const [isLoading, setIsLoading] = useState(true)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [delayPercentage, setDelayPercentage] = useState<DelayPercentage | null>(null)
  const [initialDelayPercentage, setInitialDelayPercentage] = useState('')
  const [generationDelayPercentage, setGenerationDelayPercentage] = useState('')
  const [allocationDelayPercentage, setAllocationDelayPercentage] = useState('')
  const [finishInvestigationDelayPercentage, setFinishInvestigationDelayPercentage] = useState('')
  const [initialClosureDelayPercentage, setInitialClosureDelayPercentage] = useState('')
  const [finalClosureDelayPercentage, setFinalClosureDelayPercentage] = useState('')
  const [qcDelayPercentage, setQcDelayPercentage] = useState('')
  const [missedInitiationDelayPercentage, setMissedInitiationDelayPercentage] = useState('')

  useEffect(() => {
    effectLoadDelayPercentage()
  }, [])

  useEffect(() => {
    effectOnSubmit()
  }, [isSubmitting])

  return (
    <Form loading={isLoading || isSubmitting}>
      <Header as="h3" color="grey">
        Delay Percentage Configuration
      </Header>
      <p>Configure the percentage thresholds for various delay calculations in performance evaluation.</p>

      <Divider />

      <Form.Group inline>
        <Form.Field>
          <FormLabel width="200px">Initial Delay Percentage</FormLabel>
          <Input
            label={{ basic: true, content: '%' }}
            labelPosition="right"
            value={initialDelayPercentage}
            onChange={(_, { value }) => {
              if (validator.number.isValid(value)) setInitialDelayPercentage(value)
            }}
            placeholder="0-100"
          />
        </Form.Field>
      </Form.Group>

      <Form.Group inline>
        <Form.Field>
          <FormLabel width="200px">Generation Delay Percentage</FormLabel>
          <Input
            label={{ basic: true, content: '%' }}
            labelPosition="right"
            value={generationDelayPercentage}
            onChange={(_, { value }) => {
              if (validator.number.isValid(value)) setGenerationDelayPercentage(value)
            }}
            placeholder="0-100"
          />
        </Form.Field>
      </Form.Group>

      <Form.Group inline>
        <Form.Field>
          <FormLabel width="200px">Missed Initiation Delay Percentage</FormLabel>
          <Input
            label={{ basic: true, content: '%' }}
            labelPosition="right"
            value={missedInitiationDelayPercentage}
            onChange={(_, { value }) => {
              if (validator.number.isValid(value)) setMissedInitiationDelayPercentage(value)
            }}
            placeholder="0-500"
          />
        </Form.Field>
      </Form.Group>

      <Form.Group inline>
        <Form.Field>
          <FormLabel width="200px">Allocation Delay Percentage</FormLabel>
          <Input
            label={{ basic: true, content: '%' }}
            labelPosition="right"
            value={allocationDelayPercentage}
            onChange={(_, { value }) => {
              if (validator.number.isValid(value)) setAllocationDelayPercentage(value)
            }}
            placeholder="0-100"
          />
        </Form.Field>
      </Form.Group>

      <Form.Group inline>
        <Form.Field>
          <FormLabel width="200px">Finish Investigation Delay Percentage (Fo TAT)</FormLabel>
          <Input
            label={{ basic: true, content: '%' }}
            labelPosition="right"
            value={finishInvestigationDelayPercentage}
            onChange={(_, { value }) => {
              if (validator.number.isValid(value)) setFinishInvestigationDelayPercentage(value)
            }}
            placeholder="0-100"
          />
        </Form.Field>
      </Form.Group>

      <Form.Group inline>
        <Form.Field>
          <FormLabel width="200px">Initial Closure Delay Percentage</FormLabel>
          <Input
            label={{ basic: true, content: '%' }}
            labelPosition="right"
            value={initialClosureDelayPercentage}
            onChange={(_, { value }) => {
              if (validator.number.isValid(value)) setInitialClosureDelayPercentage(value)
            }}
            placeholder="0-100"
          />
        </Form.Field>
      </Form.Group>

      <Form.Group inline>
        <Form.Field>
          <FormLabel width="200px">Final Closure Delay Percentage</FormLabel>
          <Input
            label={{ basic: true, content: '%' }}
            labelPosition="right"
            value={finalClosureDelayPercentage}
            onChange={(_, { value }) => {
              if (validator.number.isValid(value)) setFinalClosureDelayPercentage(value)
            }}
            placeholder="0-100"
          />
        </Form.Field>
      </Form.Group>

      <Form.Group inline>
        <Form.Field>
          <FormLabel width="200px">QC Delay Percentage</FormLabel>
          <Input
            label={{ basic: true, content: '%' }}
            labelPosition="right"
            value={qcDelayPercentage}
            onChange={(_, { value }) => {
              if (validator.number.isValid(value)) setQcDelayPercentage(value)
            }}
            placeholder="0-100"
          />
        </Form.Field>
      </Form.Group>

      <Button primary disabled={isSubmitButtonDisabled()} onClick={() => setIsSubmitting(true)}>
        {delayPercentage ? 'Update' : 'Add'} Configuration
      </Button>
    </Form>
  )

  async function effectLoadDelayPercentage() {
    setIsLoading(true)
    const result = await delayPercentageRepo.getDelayPercentage()
    if (result instanceof Result.Success) {
      const dp = result.data
      setDelayPercentage(dp)
      setInitialDelayPercentage(dp.getInitialDelayPercentage().toString())
      setGenerationDelayPercentage(dp.getGenerationDelayPercentage().toString())
      setAllocationDelayPercentage(dp.getAllocationDelayPercentage().toString())
      setFinishInvestigationDelayPercentage(dp.getFinishInvestigationDelayPercentage().toString())
      setInitialClosureDelayPercentage(dp.getInitialClosureDelayPercentage().toString())
      setFinalClosureDelayPercentage(dp.getFinalClosureDelayPercentage().toString())
      setQcDelayPercentage(dp.getQcDelayPercentage().toString())
      setMissedInitiationDelayPercentage(dp.getMissedInitiationDelayPercentage().toString())
    } else {
      // If not found, it's okay - user can add new configuration
      if (result.message && !result.message.includes('No delay percentage')) {
        toast.error(result.message || 'Could not load delay percentage configuration')
      }
    }
    setIsLoading(false)
  }

  async function effectOnSubmit() {
    if (!isSubmitting) return

    let result

    if (delayPercentage) {
      // Update existing
      const updateRequest: UpdateDelayPercentageRequestObject = {
        initial_delay_percentage: parseFloat(initialDelayPercentage) || 0,
        generation_delay_percentage: parseFloat(generationDelayPercentage) || 0,
        allocation_delay_percentage: parseFloat(allocationDelayPercentage) || 0,
        finish_investigation_delay_percentage: parseFloat(finishInvestigationDelayPercentage) || 0,
        initial_closure_delay_percentage: parseFloat(initialClosureDelayPercentage) || 0,
        final_closure_delay_percentage: parseFloat(finalClosureDelayPercentage) || 0,
        qc_delay_percentage: parseFloat(qcDelayPercentage) || 0,
        missed_initiation_delay_percentage: parseFloat(missedInitiationDelayPercentage) ?? 0,
        id: delayPercentage.getId(),
      }
      result = await delayPercentageRepo.updateDelayPercentage(updateRequest)
    } else {
      // Add new
      const addRequest: AddDelayPercentageRequestObject = {
        initial_delay_percentage: parseFloat(initialDelayPercentage) || 0,
        generation_delay_percentage: parseFloat(generationDelayPercentage) || 0,
        allocation_delay_percentage: parseFloat(allocationDelayPercentage) || 0,
        finish_investigation_delay_percentage: parseFloat(finishInvestigationDelayPercentage) || 0,
        initial_closure_delay_percentage: parseFloat(initialClosureDelayPercentage) || 0,
        final_closure_delay_percentage: parseFloat(finalClosureDelayPercentage) || 0,
        qc_delay_percentage: parseFloat(qcDelayPercentage) || 0,
        missed_initiation_delay_percentage: parseFloat(missedInitiationDelayPercentage) || 0,
      }
      result = await delayPercentageRepo.addDelayPercentage(addRequest)
    }

    if (result instanceof Result.Success) {
      const dp = result.data
      setDelayPercentage(dp)
      toast.success(`Successfully ${delayPercentage ? 'updated' : 'added'} delay percentage configuration`)
    } else {
      const message = result.message || 'Something went wrong'
      toast.error(message)
    }
    setIsSubmitting(false)
  }

  function isSubmitButtonDisabled() {
    return (
      isSubmitting ||
      initialDelayPercentage.trim().length === 0 ||
      generationDelayPercentage.trim().length === 0 ||
      allocationDelayPercentage.trim().length === 0 ||
      finishInvestigationDelayPercentage.trim().length === 0 ||
      initialClosureDelayPercentage.trim().length === 0 ||
      finalClosureDelayPercentage.trim().length === 0 ||
      qcDelayPercentage.trim().length === 0 ||
      missedInitiationDelayPercentage.trim().length === 0 ||
      parseFloat(initialDelayPercentage) < 0 ||
      parseFloat(initialDelayPercentage) > 100 ||
      parseFloat(generationDelayPercentage) < 0 ||
      parseFloat(generationDelayPercentage) > 100 ||
      parseFloat(allocationDelayPercentage) < 0 ||
      parseFloat(allocationDelayPercentage) > 100 ||
      parseFloat(finishInvestigationDelayPercentage) < 0 ||
      parseFloat(finishInvestigationDelayPercentage) > 100 ||
      parseFloat(initialClosureDelayPercentage) < 0 ||
      parseFloat(initialClosureDelayPercentage) > 100 ||
      parseFloat(finalClosureDelayPercentage) < 0 ||
      parseFloat(finalClosureDelayPercentage) > 100 ||
      parseFloat(qcDelayPercentage) < 0 ||
      parseFloat(qcDelayPercentage) > 100 ||
      parseFloat(missedInitiationDelayPercentage) < 0 ||
      parseFloat(missedInitiationDelayPercentage) > 100
    )
  }
}

export default DelayPercentageSegment
